import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { teamStageUploadsService } from '../../../services/teamStageUploadsService';
import { supabase } from '../../../lib/supabase';
import { format } from 'date-fns';


const ProjectOverview = ({ project, team, currentPhase }) => {
  const [additionalResources, setAdditionalResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);

  const phases = [
    { id: 1, name: 'Problem Analysis', status: 'completed', dueDate: '2025-01-15' },
    { id: 2, name: 'Solution Design', status: 'completed', dueDate: '2025-02-01' },
    { id: 3, name: 'Prototype Development', status: 'in-progress', dueDate: '2025-02-28' },
    { id: 4, name: 'MVP Development', status: 'pending', dueDate: '2025-03-31' },
    { id: 5, name: 'Final Presentation', status: 'pending', dueDate: '2025-04-15' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'in-progress': return 'text-warning bg-warning/10';
      case 'pending': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'pending': return 'Circle';
      default: return 'Circle';
    }
  };

  useEffect(() => {
    if (project?.id) {
      fetchAdditionalResources();
    }
  }, [project?.id]);

  const fetchAdditionalResources = async () => {
    try {
      setLoadingResources(true);
      // Fetch team-level uploads only (stage_id is null)
      const uploads = await teamStageUploadsService?.getTeamLevelUploads(project?.id);
      // Filter to show only PCF Admin and City Official uploads
      const filteredUploads = uploads?.filter(
        (upload) => upload?.uploaderRole === 'pcf_admin' || upload?.uploaderRole === 'city_official'
      );
      setAdditionalResources(filteredUploads || []);
    } catch (err) {
      console.error('Error fetching additional resources:', err);
    } finally {
      setLoadingResources(false);
    }
  };

  const handleDownload = async (resource) => {
    try {
      // Check if it's a link or file
      const isLink = resource?.fileUrl?.startsWith('http://') || resource?.fileUrl?.startsWith('https://');
      
      if (isLink) {
        // Open link in new tab
        window.open(resource?.fileUrl, '_blank');
      } else {
        // Download file from storage
        const { data, error } = await supabase?.storage
          ?.from('team-stage-files')
          ?.createSignedUrl(resource?.fileUrl, 60);

        if (error) throw error;

        const link = window.document?.createElement('a');
        link.href = data?.signedUrl;
        link.download = resource?.notes || 'download';
        link.target = '_blank';
        window.document?.body?.appendChild(link);
        link?.click();
        window.document?.body?.removeChild(link);
      }
    } catch (err) {
      console.error('Error accessing resource:', err);
      alert('Failed to access resource. Please try again.');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Project Overview</h2>
      </div>
      {/* Problem Statement */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-foreground mb-3">{project?.category}</h3>
        <p className="text-muted-foreground mb-4">{project?.problem_statement}</p>
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Building2" size={16} className="text-primary" />
            <span className="text-foreground">{project?.city}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="User" size={16} className="text-primary" />
            <span className="text-foreground">{project?.manager}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-primary" />
            <span className="text-foreground">Due: {project?.final_due_date}</span>
          </div>
        </div>
      </div>
      {/* Team Members */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-foreground mb-3">Team Members</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {team?.members?.map((member) => (
            <div key={member?.id} className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-foreground">
                  {member?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{member?.full_name}</p>
                <p className="text-xs text-muted-foreground">{member?.team_role}</p>
              </div>
              <div className={`w-3 h-3 rounded-full ${member?.isOnline ? 'bg-success' : 'bg-muted-foreground'}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Additional Resources Section */}
      <div>
        <h4 className="text-md font-medium text-foreground mb-3">Additional Resources</h4>
        <p className="text-xs text-muted-foreground mb-3">Uploaded by PCF Admin or City Official</p>
        {loadingResources ? (
          <div className="flex items-center justify-center py-6">
            <Icon name="Loader" size={20} className="text-primary animate-spin" />
            <span className="text-sm text-muted-foreground ml-2">Loading resources...</span>
          </div>
        ) : additionalResources?.length > 0 ? (
          <div className="space-y-2">
            {additionalResources?.map((resource) => (
              <div
                key={resource?.id}
                className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/70 transition-colors"
              >
                <div className="flex items-center space-x-3 flex-1">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon
                      name={resource?.fileUrl?.startsWith('http') ? 'Link' : 'FileText'}
                      size={18}
                      className="text-primary"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {resource?.notes || 'Untitled Resource'}
                    </p>
                    <div className="flex items-center space-x-3 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center">
                        <Icon name="User" size={12} className="mr-1" />
                        {resource?.uploaderName}
                      </span>
                      <span className="flex items-center">
                        <Icon name="Calendar" size={12} className="mr-1" />
                        {format(new Date(resource?.uploadedAt), 'MMM dd, yyyy')}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(resource)}
                  className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                  title={resource?.fileUrl?.startsWith('http') ? 'Open link' : 'Download file'}
                >
                  <Icon
                    name="ExternalLink"
                    size={16}
                    className="text-primary"
                  />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-muted/30 rounded-lg p-6 text-center">
            <Icon name="FolderOpen" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No additional resources available yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectOverview;